// src/services/api.ts
import axios from 'axios';

const API_BASE_URL = 'https://aztrainersbackend.onrender.com/api'; // Updated to match your Django server

// Types
export interface User {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  user_type: 'customer' | 'trainer' | 'admin';
  bio?: string;
  profile_picture?: string;
  date_joined: string;
  // Customer specific fields
  fitness_goals?: string;
  experience_level?: string;
  is_public?: boolean;
  preferred_workout_types?: string;
  // Trainer specific fields
  specializations?: string;
  certifications?: string;
  years_experience?: number;
  hourly_rate?: number;
  rating?: number;
  total_sessions?: number;
  is_active?: boolean;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  user_type: 'customer' | 'trainer';
  fitness_goals?: string;
  tos_accepted: boolean;
  privacy_accepted: boolean;
  
}

export interface Trainer {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  bio?: string;
  profile_picture?: string;
  specializations: string;
  years_experience: number;
  hourly_rate: number;
  rating: number;
  total_sessions: number;
}

export interface Post {
  id: string;
  author: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
  };
  content: string;
  image?: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  liked_by_user: boolean;
}

export interface BookingData {
  trainer_id: string;
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes: number;
  notes?: string;
  customer_address: string;
  total_amount: number;
  platform_fee?: number;
  trainer_amount?: number;
}

export interface ReviewData {
  rating: number;
  comment: string;
}

export interface ReviewableBooking {
  booking_id: string;
  trainer: {
    id: string;
    name: string;
    username: string;
    profile_picture?: string;
  };
  session_date: string;
  session_time: string;
  duration: number;
  total_amount: number;
  status: string;
  can_review: boolean;
}

// Stripe API Types
export interface StripeConnectStatus {
  connected: boolean;
  onboarding_complete: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  account_id?: string;
}

export interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  platform_fee: number;
  trainer_amount: number;
}

export interface LegalAgreementVersion {
  agreement_type: 'tos' | 'privacy' | 'trainer_agreement';
  current_version: string;
  effective_date: string;
}

export interface UserLegalAgreement {
  agreement_type: 'tos' | 'privacy' | 'trainer_agreement';
  version: string;
  accepted_at: string;
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for Django sessions
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include authentication
api.interceptors.request.use(
  (config) => {
    // Add any authentication token if you're using token-based auth
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    
    // Log the request for debugging
    
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
   
    return response;
  },
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data
    });
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear any stored auth data and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.reload();
    }
    
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  register: (userData: RegisterData) => api.post('/register/', userData),
  login: (credentials: LoginCredentials) => api.post('/login/', credentials),
  logout: () => api.post('/logout/'),
  getProfile: () => api.get<User>('/profile/'),
  updateProfile: (data: Partial<User>) => api.put('/profile/', data),
};

// Trainers API calls
export const trainersAPI = {
  getAll: (params: Record<string, any> = {}) => api.get<{trainers: Trainer[]; count: number}>('/trainers/', { params }),
  getById: (id: string) => api.get<Trainer>(`/trainers/${id}/`),
  getAvailableSlots: (id: string, date: string, duration = 60) => 
    api.get(`/trainers/${id}/available-slots/`, { 
      params: { date, duration } 
    }),
  getReviews: (id: string) => api.get(`/trainers/${id}/reviews/`),
};

// Reviews API calls
export const reviewsAPI = {
  // Get bookings that can be reviewed by the current customer
  getReviewableBookings: () => {
    
    return api.get<{reviewable_bookings: ReviewableBooking[]; count: number}>('/customer/reviewable-bookings/');
  },
  
  // Submit a review for a specific booking
  submitReview: (bookingId: string, reviewData: ReviewData) => {
    
    return api.post(`/bookings/${bookingId}/review/`, reviewData);
  },
  
  // Get all reviews for a trainer (uses existing trainersAPI endpoint)
  getTrainerReviews: (trainerId: string) => {
    
    return trainersAPI.getReviews(trainerId);
  },
  
  // Get customer's own reviews
  getMyReviews: () => {
    
    return api.get('/customer/reviews/');
  }
};

// Stripe API calls
export const stripeAPI = {
  // Create Stripe Connect account for trainer
  createConnectAccount: () => {
    console.log('Creating Stripe Connect account...');
    return api.post<{success: boolean; account_id: string; onboarding_url: string}>('/stripe/connect/create/');
  },
  
  // Check Stripe Connect account status
  checkConnectStatus: () => {
    console.log('Checking Stripe Connect status...');
    return api.get<StripeConnectStatus>('/stripe/connect/status/');
  },
  
  // Create payment intent for booking
  createPaymentIntent: (data: { trainer_id: string; duration_minutes: number }) => {
    console.log('Creating payment intent:', data);
    return api.post<PaymentIntentResponse>('/payments/create-intent/', data);
  },
  
  // Confirm booking after successful payment
  confirmBookingWithPayment: (data: {
    payment_intent_id: string;
    trainer_id: string;
    scheduled_date: string;
    scheduled_time: string;
    duration_minutes: number;
    customer_address: string;
    notes?: string;
  }) => {
    console.log('Confirming booking with payment:', data);
    return api.post<{success: boolean; booking_id: string; message: string}>('/bookings/confirm-with-payment/', data);
  }
};

// Social API calls
export const socialAPI = {
  getPosts: () => api.get<{posts: Post[]}>('/posts/'),
  getFollowingPosts: () => api.get<{posts: Post[]}>('/posts/following/'),
  getMutualPosts: () => api.get<{posts: Post[]}>('/posts/mutual/'),
  createPost: (content: string) => api.post('/posts/', { content }),
  likePost: (postId: string) => api.post(`/posts/${postId}/like/`),
  unlikePost: (postId: string) => api.delete(`/posts/${postId}/like/`),
  getComments: (postId: string) => api.get(`/posts/${postId}/comments/`),
  addComment: (postId: string, content: string) => api.post(`/posts/${postId}/comments/`, { content }),
  followUser: (userId: string) => api.post(`/users/${userId}/follow/`),
  unfollowUser: (userId: string) => api.delete(`/users/${userId}/follow/`),
  getUserPosts: (userId: string) => api.get(`/users/${userId}/posts/`),
};

// Admin API calls
export const adminAPI = {
  getAllUsers: () => api.get('/admin/users/'),
  getAllPosts: () => api.get('/admin/posts/'),
  deleteUser: (userId: string) => api.delete(`/admin/users/${userId}/`),
  deletePost: (postId: string) => api.delete(`/admin/posts/${postId}/`),
  deleteComment: (commentId: string) => api.delete(`/admin/comments/${commentId}/`),
  createTrainer: (trainerData: any) => api.post('/admin/trainers/create/', trainerData),
};

// Enhanced Bookings API calls with proper user context
export const bookingsAPI = {
  getMyBookings: () => {
    console.log('Fetching bookings for current user...');
    return api.get('/bookings/');
  },
  
  createBooking: (bookingData: BookingData, currentUserId?: string) => {
    console.log('Creating booking with data:', bookingData);
    console.log('Current user ID:', currentUserId);
    
    // Ensure the booking includes the current user as customer
    const bookingWithUser = {
      ...bookingData,
      // Add customer_id if provided and not already in bookingData
      ...(currentUserId && !bookingData.hasOwnProperty('customer_id') && { customer_id: currentUserId })
    };
    
    console.log('Final booking data being sent:', bookingWithUser);
    return api.post('/bookings/', bookingWithUser);
  },
  
  updateBooking: (id: string, data: any) => {
    console.log('Updating booking:', id, 'with data:', data);
    return api.put(`/bookings/${id}/`, data);
  },
  
  cancelBooking: (id: string) => {
    console.log('Cancelling booking:', id);
    return api.delete(`/bookings/${id}/`);
  },
  
  confirmBooking: (id: string) => {
    console.log('Confirming booking:', id);
    return api.post(`/bookings/${id}/confirm/`);
  },
  
  // Updated decline booking function with optional reason
  declineBooking: (bookingId: string, reason?: string) => {
    console.log('Declining booking:', bookingId, 'with reason:', reason);
    return api.post(`/bookings/${bookingId}/decline/`, { 
      reason: reason || '' 
    });
  },

  // New accept booking function
  acceptBooking: (bookingId: string) => {
    console.log('Accepting booking:', bookingId);
    return api.post(`/bookings/${bookingId}/accept/`);
  }
};

export const availabilityAPI = {
  getTrainerAvailability: (trainerId: string) => {
    console.log('Fetching availability for trainer:', trainerId);
    return api.get(`/trainers/${trainerId}/availability/`);
  },
  
  addAvailabilitySlot: (slotData: any) => {
    console.log('Adding availability slot:', slotData);
    return api.post('/trainers/availability/', slotData);
  },

  // Fix these two methods to include trainerId:
  updateAvailabilitySlot: (trainerId: string, slotId: string, slotData: any) => {
    console.log('Updating availability slot:', slotId, 'for trainer:', trainerId);
    return api.put(`/trainers/${trainerId}/availability/${slotId}/`, slotData);
  },

  deleteAvailabilitySlot: (trainerId: string, slotId: string) => {
    console.log('Deleting availability slot:', slotId, 'for trainer:', trainerId);
    return api.delete(`/trainers/${trainerId}/availability/${slotId}/`);
  },
  
  getAvailableSlots: (trainerId: string, date: string, duration: number = 60) => {
    console.log('Fetching available slots for trainer:', trainerId, 'on date:', date);
    return api.get(`/trainers/${trainerId}/available-slots/`, {
      params: { date, duration }
    });
  }
};

// Search API calls
export const searchAPI = {
  search: (query: string, type = 'all') => api.get('/search/', { 
    params: { q: query, type } 
  }),
};

// Dashboard API calls
export const dashboardAPI = {
  getDashboard: () => api.get('/dashboard/'),
};

// Helper function to get current user from localStorage
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

// Helper function to create booking with current user context
export const createBookingWithUser = (bookingData: BookingData) => {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('No authenticated user found. Please log in to create a booking.');
  }
  
  console.log('Creating booking for user:', currentUser.username);
  return bookingsAPI.createBooking(bookingData, currentUser.id);
};

export default api;